import { MenuItem as MenuItemMUI, Box, Chip } from '@mui/material';
import { Text } from '../../Text';
import { AccountLinkType, AccountMenuLink, type AccountMenuSurface } from './AccountMenuLink';

export interface AccountLinksType {
  orderHistory: AccountLinkType;
  savedOrders: AccountLinkType;
  accountDetails: AccountLinkType;
  users: AccountLinkType;
  patients: AccountLinkType;
  addressBook: AccountLinkType;
  invoices: AccountLinkType;
  messages: AccountLinkType;
  events: AccountLinkType;
  notes: AccountLinkType;
  validations?: AccountLinkType;
  helpAndSupport?: AccountLinkType;
}

export interface AccountMenuListProps {
  signOut: () => void;
  closeMenu: () => void;
  linkComponent: React.ElementType;
  links: AccountLinksType;
  isMessagesEnabled?: boolean;
  isHxCustomerService?: boolean;
  isHxInternalStaff?: boolean;
  canViewUsers?: boolean;
  /** `page`: shell background (account column). `popover`: white items for header dropdown. */
  surface?: AccountMenuSurface;
}

export const AccountMenuList = (props: AccountMenuListProps) => {
  const {
    signOut,
    closeMenu,
    linkComponent,
    links,
    isMessagesEnabled,
    isHxCustomerService,
    isHxInternalStaff,
    canViewUsers,
    surface = 'popover',
  } = props;

  const itemClick = () => {
    closeMenu();
  };

  const signOutClick = () => {
    closeMenu();
    signOut();
  };

  return (
    <Box
      tabIndex={-1}
      role="menu"
      sx={(theme) => ({
        backgroundColor:
          surface === 'page' ? theme.palette.background.default : theme.palette.background.paper,
        width: '100%',
      })}
    >
      <AccountMenuLink
        label="Order history"
        onClick={itemClick}
        link={links.orderHistory}
        linkComponent={linkComponent}
        autoFocus
        surface={surface}
      />
      <AccountMenuLink
        label="Saved orders"
        onClick={itemClick}
        link={links.savedOrders}
        linkComponent={linkComponent}
        surface={surface}
      />
      <AccountMenuLink
        label="Account"
        onClick={itemClick}
        link={links.accountDetails}
        linkComponent={linkComponent}
        surface={surface}
      />
      {canViewUsers && (
        <AccountMenuLink
          label="Users"
          onClick={itemClick}
          link={links.users}
          linkComponent={linkComponent}
          surface={surface}
        />
      )}
      <AccountMenuLink
        label="Patients"
        onClick={itemClick}
        link={links.patients}
        linkComponent={linkComponent}
        surface={surface}
      />
      <AccountMenuLink
        label="Address book"
        onClick={itemClick}
        link={links.addressBook}
        linkComponent={linkComponent}
        surface={surface}
      />
      <AccountMenuLink
        label="Invoices"
        onClick={itemClick}
        link={links.invoices}
        linkComponent={linkComponent}
        surface={surface}
      />
      {isMessagesEnabled && (
        <AccountMenuLink
          label="Messages"
          onClick={itemClick}
          link={links.messages}
          linkComponent={linkComponent}
          surface={surface}
        >
          <Chip
            label={0}
            size={'small'}
            color="softBlue"
            sx={{
              fontSize: '12px',
              fontWeight: 'bold',
              marginLeft: 'auto',
            }}
          />
        </AccountMenuLink>
      )}
      {isHxCustomerService && (
        <>
          <AccountMenuLink
            label="Events"
            onClick={itemClick}
            link={links.events}
            linkComponent={linkComponent}
            surface={surface}
          />
          <AccountMenuLink
            label="Notes"
            onClick={itemClick}
            link={links.notes}
            linkComponent={linkComponent}
            surface={surface}
          />
        </>
      )}
      {isHxInternalStaff && links.validations && (
        <AccountMenuLink
          label="Validations"
          onClick={itemClick}
          link={links.validations}
          linkComponent={linkComponent}
          surface={surface}
        />
      )}
      {links.helpAndSupport && (
        <AccountMenuLink
          label="Help & Support"
          onClick={itemClick}
          link={links.helpAndSupport}
          linkComponent={linkComponent}
          surface={surface}
        />
      )}
      <MenuItemMUI
        onClick={signOutClick}
        sx={(theme) => ({
          paddingX: 2,
          paddingY: 1,
          backgroundColor:
            surface === 'page' ? theme.palette.background.default : theme.palette.background.paper,
        })}
        tabIndex={0}
      >
        <Text variant="p2" color="red">
          Sign out
        </Text>
      </MenuItemMUI>
    </Box>
  );
};
